import axios from 'axios';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { extractErrorContent, getErrMessages } from './handlers';

const requestState = {
  waiting: 'WAITING',
  success: 'SUCCESS',
  error: 'ERROR',
};

const defaultOptions = () => ({
  onError: undefined,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onForbidden: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onLog: () => {},

  onSuccess: () => {},
});

export const useRequest = (requestFunction, entryOptions = {}) => {
  const options = _.merge({}, defaultOptions(), entryOptions);
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState();
  const [errorResponse, setErrorResponse] = useState([]);
  const [status, setStatus] = useState(requestState.waiting);

  useEffect(() => {
    if (status !== requestState.error || errorResponse.length < 1) return;
    const msg = errorResponse.join(' ');
    if (typeof options.onError === 'function') {
      options.onError(msg);
      return;
    }
    console.error(msg);
  }, [status, errorResponse]);

  const makeRequest = async (...args) => {
    setStatus(requestState.waiting);
    setIsLoading(true);
    let isOk = false;
    let successContent;
    let errorContent;
    let messages = [];

    if (typeof requestFunction !== 'function')
      throw new Error(`useRequest param 'requestFunction' must be a function`);
    try {
      const wrappedResponse = await requestFunction(...args);

      options.onLog(JSON.stringify(wrappedResponse, null, 3));

      const isValid = _.conformsTo(wrappedResponse, {
        success: (value) => _.isBoolean(value),
        data: (value) => true,
        messages: (value) => _.isArray(value),
      });

      if (!isValid) {
        isOk = false;
        setResponse(undefined);
        throw new Error('Respuesta inválida del servidor', wrappedResponse);
      }

      const { success, data, messages: reqMessages } = wrappedResponse;

      const safeMessages = Array.isArray(reqMessages)
        ? reqMessages.filter(Boolean)
        : reqMessages
        ? [reqMessages]
        : [];

      messages = safeMessages.length > 0 ? safeMessages : ['Operación exitosa'];

      isOk = success === true;

      if (!isOk) {
        successContent = undefined;
        setResponse(undefined);
        setStatus(requestState.error);
        errorContent = { messages };
        setErrorResponse(messages);
      } else {
        successContent = data;
        setResponse(data);
        setStatus(requestState.success);
        errorContent = undefined;
        setErrorResponse([]);
        if (typeof options.onSuccess === 'function') options.onSuccess(messages.join(' '));
      }
    } catch (error) {
      setResponse(undefined);
      console.error(error);
      const isRealError = axios.isAxiosError(error);

      messages = isRealError
        ? getErrMessages(error)
        : ['Error interno al realizar petición al servidor'];

      const isForbidden =
        isRealError &&
        error.response &&
        (error.response.status === 401 || error.response.status === 403);

      if (isForbidden && typeof options.onForbidden === 'function') {
        options.onForbidden(messages.join(' '));
      }

      errorContent = extractErrorContent(error);

      setErrorResponse(messages);
      setStatus(requestState.error);
      options.onLog(JSON.stringify(errorContent, null, 3));

      successContent = undefined;
      isOk = false;
    } finally {
      setIsLoading(false);
    }
    return { isOk, successContent, errorContent, messages };
  };

  return { makeRequest, isLoading, response, errorResponse, status };
};
